/**
 * Las conversaciones abiertas, por encima del panel.  (spec 073)
 *
 * Viven en el armazón, no en la página: cambiar de ruta no las cierra.
 */
import { useQuery } from '@tanstack/react-query'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type PointerEvent as ReactPointerEvent,
    type ReactNode,
} from 'react'

import { api } from '../lib/api'
import { emparejarPasosMensajes } from '../lib/camino-mensaje'
import { cn } from '../lib/cn'
import { CAJA_CONSEGUIDO, PUNTO_CONSEGUIDO, TEXTO_CONSEGUIDO, type Conseguido } from '../lib/conseguido'
import { AvisoCitaAgenda, type CitaEnAgenda } from './AvisoCitaAgenda'
import { fechaHora, hora, cuandoCita } from '../lib/tiempo'
import {
    firmaMensajes,
    posicionInicial,
    reducirHilos,
    type AccionHilo,
    type HiloFlotante,
} from '../lib/hilos-flotantes'
import { useRefrescoHiloAbierto } from '../lib/use-refresco-hilo'
import { ControlDelHilo } from './ControlDelHilo'
import { MensajesConversacion } from './MensajesConversacion'
import type { SobreMensajes } from '../lib/tipos'

interface PasoCamino {
    etiqueta: string
    at: string | null
    tono: Conseguido | null
    hecho: string
}

interface GestionCita {
    tipo: Conseguido
    fecha: string | null
    hora: string | null
    servicio: string | null
    profesional: string | null
}

interface VistaMinima {
    persona: string | null
    telefono: string | null
    punto: string
    lastObservedAt: string
    mando: 'agendify' | 'equipo' | 'cerrada' | null
    messages: SobreMensajes
    camino: PasoCamino[]
    gestion: GestionCita | null
    citaEnAgenda?: CitaEnAgenda
}

interface AccionesHilo {
    abrir: (accountId: number, conversationId: number) => void
    despachar: (accion: AccionHilo) => void
}

const AccionesCtx = createContext<AccionesHilo | null>(null)
const ListaCtx = createContext<readonly HiloFlotante[]>([])

export function HilosFlotantesProvider({ children }: { children: ReactNode }) {
    const [lista, setLista] = useState<readonly HiloFlotante[]>([])
    const despachar = useCallback((accion: AccionHilo) => {
        setLista((prev) => reducirHilos(prev, accion))
    }, [])
    const abrir = useCallback((accountId: number, conversationId: number) => {
        setLista((prev) => {
            const { x, y } = posicionInicial(prev.length, window.innerWidth, window.innerHeight)
            return reducirHilos(prev, { tipo: 'abrir', accountId, conversationId, x, y })
        })
    }, [])
    const acciones = useMemo(() => ({ abrir, despachar }), [abrir, despachar])
    return (
        <AccionesCtx.Provider value={acciones}>
            <ListaCtx.Provider value={lista}>
                {children}
                <Muelle />
            </ListaCtx.Provider>
        </AccionesCtx.Provider>
    )
}

export function useAbrirHilo(): (accountId: number, conversationId: number) => void {
    const ctx = useContext(AccionesCtx)
    if (!ctx) throw new Error('useAbrirHilo fuera del panel')
    return ctx.abrir
}

function Muelle() {
    const lista = useContext(ListaCtx)
    const acciones = useContext(AccionesCtx)
    if (!acciones || lista.length === 0) return null
    const minimizados = lista.filter((h) => h.minimizado)
    const abiertos = lista.filter((h) => !h.minimizado)
    return (
        <>
            {abiertos.map((h) => (
                <Ventana key={`${h.accountId}:${h.conversationId}`} hilo={h} acciones={acciones} />
            ))}
            {minimizados.length > 0 && (
                <div className="fixed bottom-3 right-3 z-40 flex max-w-[calc(100vw-1.5rem)] flex-row-reverse flex-wrap-reverse items-end gap-2">
                    {minimizados.map((h) => (
                        <Tarjeta key={`${h.accountId}:${h.conversationId}`} hilo={h} acciones={acciones} />
                    ))}
                </div>
            )}
        </>
    )
}

function useAnchoVentana(): number {
    const [ancho, setAncho] = useState(() => window.innerWidth)
    useEffect(() => {
        const alCambiar = () => setAncho(window.innerWidth)
        window.addEventListener('resize', alCambiar)
        return () => window.removeEventListener('resize', alCambiar)
    }, [])
    return ancho
}

function useHilo(accountId: number, conversationId: number) {
    const q = useQuery({
        queryKey: ['business-conversation', accountId, conversationId],
        queryFn: () => api.get<VistaMinima>(`/business/${accountId}/conversations/${conversationId}`),
    })
    useRefrescoHiloAbierto(() => {
        void q.refetch()
    })
    return q
}

function Vigia({
    hilo,
    firma,
    listo,
    despachar,
}: {
    hilo: HiloFlotante
    firma: string
    listo: boolean
    despachar: (accion: AccionHilo) => void
}) {
    useEffect(() => {
        if (!listo) return
        const comun = { accountId: hilo.accountId, conversationId: hilo.conversationId, firma }
        if (hilo.minimizado) despachar({ tipo: 'comparar', ...comun })
        else despachar({ tipo: 'visto', ...comun })
    }, [listo, firma, hilo.minimizado, hilo.accountId, hilo.conversationId, despachar])
    return null
}

function Ventana({ hilo, acciones }: { hilo: HiloFlotante; acciones: AccionesHilo }) {
    const q = useHilo(hilo.accountId, hilo.conversationId)
    const [caminoAbierto, setCaminoAbierto] = useState(false)
    const [marca, setMarca] = useState<{ i: number; k: number } | null>(null)
    const vw = useAnchoVentana()
    const msgs = q.data?.messages.status === 'ok' ? (q.data.messages.data ?? []) : []
    const firma = firmaMensajes(msgs)
    const titulo = q.data?.persona ?? `Conversación #${hilo.conversationId}`
    const indices = emparejarPasosMensajes(msgs, q.data?.camino ?? [])
    const ancho = Math.min(caminoAbierto ? 720 : 440, vw - 16)
    const left = Math.max(8, Math.min(hilo.x, vw - ancho - 8))

    const irAlPaso = (iPaso: number) => {
        const i = indices[iPaso]
        if (i === null || i === undefined) return
        const k = Date.now()
        setMarca({ i, k })
        window.setTimeout(() => {
            setMarca((m) => (m && m.k === k ? null : m))
        }, 2800)
    }

    const arrastrar = (e: ReactPointerEvent) => {
        if ((e.target as HTMLElement).closest('button, a, input, textarea')) return
        acciones.despachar({ tipo: 'frente', accountId: hilo.accountId, conversationId: hilo.conversationId })
        const origen = { x: e.clientX, y: e.clientY, ox: left, oy: hilo.y }
        const mover = (ev: PointerEvent) => {
            const x = Math.max(8, Math.min(origen.ox + ev.clientX - origen.x, window.innerWidth - 120))
            const y = Math.max(8, Math.min(origen.oy + ev.clientY - origen.y, window.innerHeight - 80))
            acciones.despachar({
                tipo: 'mover',
                accountId: hilo.accountId,
                conversationId: hilo.conversationId,
                x,
                y,
            })
        }
        const soltar = () => {
            window.removeEventListener('pointermove', mover)
            window.removeEventListener('pointerup', soltar)
        }
        window.addEventListener('pointermove', mover)
        window.addEventListener('pointerup', soltar)
    }

    return (
        <section
            className="fixed flex flex-col overflow-hidden rounded-lg border border-neutral-700 bg-neutral-950 shadow-2xl transition-[width,left] duration-200 motion-reduce:transition-none"
            style={{
                left,
                top: hilo.y,
                width: ancho,
                height: 'min(620px, calc(100vh - 5rem))',
                zIndex: hilo.z,
            }}
            role="dialog"
            aria-modal="false"
            aria-label={titulo}
            onPointerDown={() =>
                acciones.despachar({ tipo: 'frente', accountId: hilo.accountId, conversationId: hilo.conversationId })
            }
        >
            <Vigia hilo={hilo} firma={firma} listo={q.isSuccess} despachar={acciones.despachar} />
            <header
                className="flex cursor-grab select-none items-start justify-between gap-2 border-b border-neutral-800 px-3 py-2 active:cursor-grabbing"
                onPointerDown={arrastrar}
            >
                <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-neutral-100">{titulo}</h3>
                    <p className="truncate text-xs text-neutral-500">{q.data?.telefono ?? 'sin teléfono'}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                    <button
                        type="button"
                        aria-expanded={caminoAbierto}
                        className={cn(
                            'rounded border px-2 py-1 text-xs',
                            caminoAbierto
                                ? 'border-neutral-500 text-neutral-100'
                                : 'border-neutral-700 text-neutral-400 hover:text-neutral-200',
                        )}
                        onClick={() => setCaminoAbierto((v) => !v)}
                    >
                        Camino
                    </button>
                    <button
                        type="button"
                        className="rounded border border-neutral-700 px-2 py-1 text-xs text-neutral-400 hover:text-neutral-200"
                        onClick={() =>
                            acciones.despachar({
                                tipo: 'minimizar',
                                accountId: hilo.accountId,
                                conversationId: hilo.conversationId,
                            })
                        }
                    >
                        Minimizar
                    </button>
                    <button
                        type="button"
                        className="rounded border border-neutral-700 px-2 py-1 text-xs text-neutral-400 hover:text-neutral-200"
                        onClick={() =>
                            acciones.despachar({
                                tipo: 'cerrar',
                                accountId: hilo.accountId,
                                conversationId: hilo.conversationId,
                            })
                        }
                    >
                        Cerrar
                    </button>
                </div>
            </header>
            <div className="flex min-h-0 flex-1">
                <div className="flex min-w-0 flex-1 flex-col p-3">
                    {q.isPending && <p className="text-sm text-neutral-500">Cargando conversación…</p>}
                    {q.isError && <p className="text-sm text-rose-400">No se pudo abrir esta conversación.</p>}
                    {q.data && (
                        <>
                            <MensajesConversacion
                                sobre={q.data.messages}
                                onReintentar={() => void q.refetch()}
                                recargando={q.isFetching}
                                resaltar={marca?.i ?? null}
                                resaltarClave={marca?.k ?? 0}
                                idHilo={hilo.conversationId}
                                encajar
                            />
                            <ControlDelHilo
                                accountId={hilo.accountId}
                                conversationId={hilo.conversationId}
                                mando={q.data.mando}
                                onHecho={() => void q.refetch()}
                            />
                        </>
                    )}
                </div>
                {caminoAbierto && q.data && (
                    <CaminoDelBot
                        camino={q.data.camino}
                        gestion={q.data.gestion}
                        citaEnAgenda={q.data.citaEnAgenda}
                        punto={q.data.punto}
                        lastObservedAt={q.data.lastObservedAt}
                        onPaso={irAlPaso}
                        casables={indices.map((i) => i !== null)}
                    />
                )}
            </div>
        </section>
    )
}

function PasoDelCamino({
    paso,
    casable,
    onClick,
}: {
    paso: PasoCamino
    casable: boolean
    onClick: () => void
}) {
    const clase = cn(
        'flex w-full items-start gap-2 rounded px-1 py-1 text-left text-xs',
        casable
            ? 'cursor-pointer bg-amber-400/10 ring-1 ring-amber-300/40 hover:bg-amber-400/20'
            : 'cursor-default opacity-45',
    )
    const interior = (
        <>
            <span
                className={cn(
                    'mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full',
                    paso.tono ? PUNTO_CONSEGUIDO[paso.tono] : 'bg-neutral-600',
                )}
            />
            <span className={paso.tono ? TEXTO_CONSEGUIDO[paso.tono] : 'text-neutral-300'}>{paso.etiqueta}</span>
            {paso.at && <span className="ml-auto shrink-0 font-mono text-neutral-600">{hora(paso.at)}</span>}
        </>
    )
    if (!casable) return <div className={clase}>{interior}</div>
    return (
        <button type="button" className={clase} onClick={onClick}>
            {interior}
        </button>
    )
}

function CaminoDelBot({
    camino,
    gestion,
    citaEnAgenda,
    punto,
    lastObservedAt,
    onPaso,
    casables,
}: {
    camino: PasoCamino[]
    gestion: GestionCita | null
    citaEnAgenda: CitaEnAgenda | undefined
    punto: string
    lastObservedAt: string
    onPaso: (i: number) => void
    casables: boolean[]
}) {
    return (
        <aside className="flex w-64 shrink-0 flex-col overflow-y-auto border-l border-neutral-800 p-3">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                Qué hace el bot
            </h4>
            <p className="mb-3 text-xs text-neutral-400">
                {punto}
                <span className="mt-0.5 block font-mono text-neutral-600">{fechaHora(lastObservedAt)}</span>
            </p>
            {gestion && (
                <div className={cn('mb-3 rounded-lg border px-3 py-2 text-xs', CAJA_CONSEGUIDO[gestion.tipo])}>
                    {fraseGestion(gestion)}
                    <AvisoCitaAgenda cita={citaEnAgenda} />
                </div>
            )}
            {camino.length === 0 ? (
                <p className="text-xs text-neutral-600">Aún no hay camino apuntado para esta conversación.</p>
            ) : (
                <ol className="space-y-1">
                    {camino.map((p, i) => (
                        <li key={`${p.hecho}-${p.at ?? i}`}>
                            <PasoDelCamino paso={p} casable={casables[i] === true} onClick={() => onPaso(i)} />
                        </li>
                    ))}
                </ol>
            )}
        </aside>
    )
}

function fraseGestion(g: GestionCita): string {
    const acto = g.tipo === 'reserva' ? 'Cita creada' : g.tipo === 'cancelacion' ? 'Cita anulada' : 'Cita cambiada'
    const cuando = g.fecha && g.hora ? ` ${cuandoCita(g.fecha, g.hora)}` : ''
    const con = g.profesional ? `, con ${g.profesional}` : ''
    return `${acto}${cuando}${con}`
}

function Tarjeta({ hilo, acciones }: { hilo: HiloFlotante; acciones: AccionesHilo }) {
    const q = useHilo(hilo.accountId, hilo.conversationId)
    const msgs = q.data?.messages.status === 'ok' ? (q.data.messages.data ?? []) : []
    const firma = firmaMensajes(msgs)
    const titulo = q.data?.persona ?? `#${hilo.conversationId}`
    return (
        <div className={cn('flex items-stretch overflow-hidden rounded-lg border border-neutral-700 bg-neutral-950 shadow-xl', hilo.parpadea && 'hilo-parpadea')}>
            <Vigia hilo={hilo} firma={firma} listo={q.isSuccess} despachar={acciones.despachar} />
            <button
                type="button"
                className="min-w-0 px-3 py-2 text-left"
                onClick={() =>
                    acciones.despachar({
                        tipo: 'restaurar',
                        accountId: hilo.accountId,
                        conversationId: hilo.conversationId,
                    })
                }
            >
                <span className="block max-w-40 truncate text-sm font-medium text-neutral-100">{titulo}</span>
                <span className="block max-w-40 truncate text-xs text-neutral-500">
                    {hilo.parpadea ? 'Mensaje nuevo' : (q.data?.telefono ?? 'Conversación')}
                </span>
            </button>
            <button
                type="button"
                aria-label={`Cerrar ${titulo}`}
                className="border-l border-neutral-800 px-2 text-xs text-neutral-500 hover:text-neutral-200"
                onClick={() =>
                    acciones.despachar({
                        tipo: 'cerrar',
                        accountId: hilo.accountId,
                        conversationId: hilo.conversationId,
                    })
                }
            >
                ×
            </button>
        </div>
    )
}
